import { Coins } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { BillingDetailsSection } from 'features/subscription/components/billing-overview';
import { useGetBillingProfile } from 'features/subscription/hooks/use-subscription';
import { useCreditSummary } from '../hooks/use-credits';
import { BalanceCard, BalanceCardSkeleton } from './balance-card';
import { CreditHistory } from './credit-history';
import { SectionError } from './section-error';
import { TopUpForm } from './topup-form';

const Note = ({ children }: { children: string }) => (
  <Card className="border-border/70 shadow-none">
    <CardContent className="flex items-center gap-3 text-sm text-muted-foreground">
      <Coins aria-hidden="true" className="h-5 w-5 shrink-0" />
      {children}
    </CardContent>
  </Card>
);

/** The Credits tab: balance, top-up, billing details and history. */
export const CreditsPanel = () => {
  const { t } = useTranslation();
  const summary = useCreditSummary();
  const profile = useGetBillingProfile();

  const renderBody = () => {
    if (summary.isLoading) {
      return (
        <div className="space-y-6" aria-busy="true">
          <BalanceCardSkeleton />
          <Skeleton className="h-72 w-full" />
        </div>
      );
    }
    if (summary.isError || !summary.data) {
      return (
        <SectionError
          message={t('CREDITS_LOAD_FAILED', 'Your credits could not be loaded.')}
          onRetry={() => summary.refetch()}
        />
      );
    }
    if (!summary.data.canTopUp) {
      return (
        <>
          <BalanceCard summary={summary.data} />
          <Note>{t('CREDITS_ADMIN_ONLY', 'Only organization admins can top up credits.')}</Note>
        </>
      );
    }
    return (
      <>
        <BalanceCard summary={summary.data} />
        <TopUpForm summary={summary.data} />
        {profile.data?.isComplete && <BillingDetailsSection />}
        <CreditHistory />
      </>
    );
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h2 className="mb-1 text-lg font-semibold text-foreground sm:text-xl">
          {t('CREDITS', 'Credits')}
        </h2>
        <p className="text-xs text-muted-foreground sm:text-sm">
          {t(
            'CREDITS_TAB_DESCRIPTION',
            'Top up credits for your whole organization. They never expire.'
          )}
        </p>
      </div>
      {renderBody()}
    </div>
  );
};
